import {
  Stack,
  TextField,
  Chip,
  Typography,
  InputAdornment,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import { useMemo } from "react";
import { useTenantTranslation } from "hooks/formatters";
import { useListCameraAnglesQuery } from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";

type IdentifyEventsAdminFieldsProps = Readonly<{
  location: string;
  requiredAngles: readonly string[];
  setRequiredAngles: (angles: string[]) => void;
  maxDurationSeconds: number;
  setMaxDurationSeconds: (seconds: number) => void;
  allowedCoverageGapSeconds: number;
  setAllowedCoverageGapSeconds: (seconds: number) => void;
  ignoreCoverage: boolean;
  setIgnoreCoverage: (value: boolean) => void;
}>;

export default function IdentifyEventsAdminFields({
  location,
  requiredAngles,
  setRequiredAngles,
  maxDurationSeconds,
  setMaxDurationSeconds,
  allowedCoverageGapSeconds,
  setAllowedCoverageGapSeconds,
  ignoreCoverage,
  setIgnoreCoverage,
}: IdentifyEventsAdminFieldsProps) {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();

  const { data: allAngles } = useListCameraAnglesQuery({ tenantName });

  const availableAngles = useMemo<string[]>(() => {
    if (!allAngles) return [];
    return allAngles
      .filter((angle) => angle.location_name === location)
      .map((angle) => angle.name);
  }, [allAngles, location]);

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" gutterBottom>
          {t("requiredAngles")}
        </Typography>

        <Autocomplete
          multiple
          options={availableAngles}
          value={[...requiredAngles]}
          onChange={(_, newValue: string[]) => {
            setRequiredAngles(newValue);
          }}
          renderValue={(value: readonly string[], getTagProps) =>
            value.map((option: string, index: number) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option}
                  {...tagProps}
                  color="primary"
                  variant="outlined"
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField {...params} placeholder={t("selectAngles")} />
          )}
        />
      </Stack>

      <TextField
        label={t("maxDuration")}
        type="number"
        value={maxDurationSeconds}
        onChange={(e) => {
          setMaxDurationSeconds(Number(e.target.value));
        }}
        sx={{ alignSelf: "flex-start" }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">{t("seconds")}</InputAdornment>
            ),
          },
        }}
      />

      <TextField
        label={t("allowedCoverageGap")}
        type="number"
        value={allowedCoverageGapSeconds}
        onChange={(e) => {
          setAllowedCoverageGapSeconds(Number(e.target.value));
        }}
        sx={{ alignSelf: "flex-start" }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">{t("seconds")}</InputAdornment>
            ),
          },
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={ignoreCoverage}
            onChange={(e) => {
              setIgnoreCoverage(e.target.checked);
            }}
            data-testid="ignore-coverage-checkbox"
          />
        }
        label={t("ignoreVideoCoverage")}
      />
    </Stack>
  );
}
